import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable, observable } from "mobx";
import type * as React from "react";
import { v4 as uuid } from "uuid";

type MinimizeModalOptions = {
  title: string;
  onMaximize: () => void;
};

type WarningModalData = {
  modalId: string;
  onConfirm: () => void;
};

export class DialogsStore {
  @observable
  modalStack = new Map<string, React.ReactElement>();

  @observable
  minimizedModals = new Map<string, MinimizeModalOptions>();

  warningModalDisclosure = new DisclosureState<WarningModalData>();

  constructor() {
    makeObservable(this);
  }

  @action.bound openModal = ({
    id,
    content,
    replace,
  }: {
    id?: string;
    content: React.ReactElement;
    replace?: boolean;
  }) => {
    const modalId = id ?? uuid();

    if (replace) {
      this.closeAllModals();
    }

    this.modalStack.set(modalId, content);
  };

  @action closeModal = (id: string) => {
    this.modalStack.delete(id);
    this.minimizedModals.delete(id);
  };

  @action closeAllModals = () => {
    this.modalStack.clear();
    this.minimizedModals.clear();
  };

  @computed
  get areModalsMinimized(): boolean {
    return this.modalStack.size > 0 && this.minimizedModals.size > 0;
  }

  @action.bound
  minimizeModal(id: string, options: MinimizeModalOptions): void {
    this.minimizedModals.set(id, options);
  }

  @action.bound
  maximizeModal(id: string): void {
    this.minimizedModals.delete(id);
  }

  @action.bound
  handleWarningModalConfirm(): void {
    if (!this.warningModalDisclosure.additionalData) return;
    this.closeModal(this.warningModalDisclosure.additionalData.modalId);
    this.warningModalDisclosure.additionalData.onConfirm();
  }
}
